import React, { useState } from 'react';

import { getTopUpCreditAmount } from '../../utils/creditGate';
import { openReferEarn } from '../../utils/freeCreditsPrompts';
import { BillingModal } from '../BillingModal';

import type { PlanningCreditRefusalDetail } from './planningCreditRefusalError';

/** "Next free credits" (Free plan) or "Next plan credits" (paid plan) in the refusal's language. */
const NEXT_GRANT_LABEL: Record<'free' | 'plan', Record<string, string>> = {
  free: {
    en: 'Next free credits',
    ar: 'الرصيد المجاني التالي',
    es: 'Próximos créditos gratis',
    pt: 'Próximos créditos grátis',
    fr: 'Prochains crédits gratuits',
    hi: 'अगले मुफ़्त क्रेडिट',
    bn: 'পরবর্তী বিনামূল্যের ক্রেডিট',
  },
  plan: {
    en: 'Next plan credits',
    ar: 'رصيد الخطة التالي',
    es: 'Próximos créditos del plan',
    pt: 'Próximos créditos do plano',
    fr: 'Prochains crédits du forfait',
    hi: 'अगले प्लान क्रेडिट',
    bn: 'পরবর্তী প্ল্যান ক্রেডিট',
  },
};
/** An overdue grant has not landed yet: say it is due, never a time in the past. */
const DUE_NOW: Record<string, string> = {
  en: 'due now', ar: 'قريبًا', es: 'en breve', pt: 'em breve', fr: 'sous peu', hi: 'जल्द ही', bn: 'শীঘ্রই',
};

/** Pins the clock in tests; the page uses the viewer's own locale, time zone and clock. */
export interface GrantLineOptions {
  locale?: string;
  timeZone?: string;
  now?: number;
}

const grantTime = (when: Date, language: string, options: GrantLineOptions): string => {
  if (when.getTime() <= (options.now ?? Date.now())) return DUE_NOW[language] ?? DUE_NOW.en;

  return new Intl.DateTimeFormat(options.locale ?? (language === 'en' ? undefined : language), {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: options.timeZone,
  }).format(when);
};

/** When the plan's next credits land, on the viewer's own clock: every stored time zone is UTC. */
export const nextGrantLine = (refusal: PlanningCreditRefusalDetail, options: GrantLineOptions = {}): string | null => {
  const grant = refusal.next_grant;
  const when = grant ? new Date(grant.scheduled_at) : null;

  if (!grant || !when || Number.isNaN(when.getTime())) return null;
  const language = refusal.language ?? 'en';
  const labels = NEXT_GRANT_LABEL[grant.kind === 'plan' ? 'plan' : 'free'];
  const time = grant.minutes <= 0 ? (DUE_NOW[language] ?? DUE_NOW.en) : grantTime(when, language, options);

  return `${labels[language] ?? labels.en}: +${grant.amount} · ${time}`;
};

/**
 * The refusal under the chat's action bar: what the press needs, the balance, when the plan's next credits arrive,
 * and the ways on now (Add Credits, Upgrade, Free credits). Nothing restarts by itself; the user presses again.
 */
export const PlanningCreditRefusal: React.FC<{ refusal: PlanningCreditRefusalDetail }> = ({ refusal }) => {
  const [billing, setBilling] = useState<'credits' | 'plans' | null>(null);
  const grantLine = nextGrantLine(refusal);

  return (
    <div className="agent-suggestion-credit-refusal" role="alert">
      <p className="agent-suggestion-credit-refusal__message">{refusal.message}</p>
      {grantLine && (
        <p className="agent-suggestion-credit-refusal__grant">
          <i className="fa-solid fa-gift" aria-hidden="true" />
          <span>{grantLine}</span>
        </p>
      )}
      <div className="agent-suggestion-pills">
        {refusal.add_credits !== false && (
          <button type="button" className="ai-overview-action-btn ai-overview-action-btn--add-credit"
            onClick={() => setBilling('credits')}>
            Add Credits
          </button>
        )}
        {refusal.upgrade_available && (
          <button type="button" className="ai-overview-action-btn ai-overview-action-btn--upgrade"
            onClick={() => setBilling('plans')}>
            Upgrade
          </button>
        )}
        <button type="button" className="ai-overview-action-btn ai-overview-action-btn--free-credits"
          onClick={openReferEarn}>
          Free credits
        </button>
      </div>
      {billing && (
        <BillingModal
          initialView={billing}
          initialCreditAmount={billing === 'credits' ? getTopUpCreditAmount({ shortfall: refusal.shortfall }) : undefined}
          onClose={() => setBilling(null)}
        />
      )}
    </div>
  );
};
